import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useVerification } from "@hooks/use_verification";
import useTasks from "@hooks/use_tasks";
import IdentityVerificationModal from "@components/IdentityVerificationModal";
import type { GhrTask } from "@/types/task";

// Packages the verify-modal state machine every task surface repeats: modal
// state, task invalidation on success, the auto-open guard, and the modal itself.
export const useVerificationFlow = (
  task: GhrTask,
  { autoOpen = true }: { autoOpen?: boolean } = {},
) => {
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();
  const { isRefetching } = useTasks();

  const { verifyIdentity, isLoading } = useVerification({
    taskId: task.taskable_id,
    onSuccess: () => {
      setShowModal(false);
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  useEffect(() => {
    // Skip while refetching: cached data can briefly show auto_open_modal true.
    if (
      autoOpen &&
      task.auto_open_modal &&
      !isRefetching &&
      task.status !== "awaiting_user_input"
    ) {
      setShowModal(true);
    }
  }, [autoOpen, task.auto_open_modal, isRefetching, task.status]);

  const handleVerify = async () => {
    setShowModal(false);
    await verifyIdentity();
  };

  const openModal = () => setShowModal(true);

  const modal = (
    <IdentityVerificationModal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      onVerify={handleVerify}
      companyName={task.company_name}
      partner={task.partner}
    />
  );

  return { openModal, modal, isLoading };
};

export default useVerificationFlow;
